import { useMemo } from "react";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { PageLayout } from "../../shared/layouts/PageLayout";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import { Checkbox, SubmitButton } from "@avenue-8/sidekick-ui";
import { adminDashboardBreadcrumbs } from "../../features/AdminDashboard/admin-dashboard-breadcrumbs";
import {
  CREATE_USER_SCHEMA_DEFAULT_VALUES,
  CreateUserFormSchema,
  createUserFormSchema,
} from "../../features/Users/create-user-form-helpers";
import { TextField } from "../../shared/components/TextField";
import { Form } from "../../shared/components/Form";
import { FormErrorMessage } from "../../shared/components/FormErrorMessage";
import { extendedTimezones, timezones } from "@shared/utils/data/timezones";
import { usaStates } from "@shared/utils/data/usaStates";
import { PhoneNumberTextField } from "@shared/components/PhoneNumberTextField";
import { useCreateUser } from "@queries/users/useCreateUser";
import { useGetSubscriptionPlans } from "@queries/subscription-plans/useGetSubscriptionPlans";

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 2rem;
  border-radius: 1rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  width: 100%;

  ${(p) => p.theme.media.mobileDown} {
    padding: 1.5rem 1rem;
    gap: 1.5rem;
    grid-template-columns: 1fr;

    > :last-child {
      order: -1;
    }
  }
`;

export function CreateUser() {
  const [searchParams] = useSearchParams();
  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
  } = useForm<CreateUserFormSchema>({
    defaultValues: {
      ...CREATE_USER_SCHEMA_DEFAULT_VALUES,
    },
    resolver: yupResolver(createUserFormSchema),
    reValidateMode: "onChange",
  });

  const emailConfirmed = watch("emailConfirmed");
  const subscriptionPlanId = watch("subscriptionPlanId");

  const breadcrumbs = {
    current: {
      label: "Create",
    },
    previous: [
      adminDashboardBreadcrumbs.dashboard,
      adminDashboardBreadcrumbs.users,
    ],
  };

  const createUserMutation = useCreateUser();
  const subscriptionPlansQuery = useGetSubscriptionPlans();

  const subscriptionPlans = useMemo(
    () =>
      [...(subscriptionPlansQuery.data?.items || [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [subscriptionPlansQuery.data?.items],
  );

  const selectedPlanAllowedProviders =
    subscriptionPlans.find((plan) => plan.id === subscriptionPlanId)?.access
      .allowedMlsProviders ?? [];

  const onSubmit = async (data: CreateUserFormSchema) => {
    if (!isDirty) return;
    await createUserMutation.mutateAsync({
      name: data.name,
      email: data.email,
      timezone: data.timezone === "auto-detect" ? undefined : data.timezone,
      countryState: data.countryState,
      mlsId: data.mlsId,
      phoneNumber: data.phoneNumber,
      officeName: data.officeName,
      emailConfirmed: data.emailConfirmed,
      enabledMlsProviders: data.enabledMlsProvidersSelected,
      subscriptionPlanId: data.subscriptionPlanId,
    });
    reset({ ...CREATE_USER_SCHEMA_DEFAULT_VALUES });
  };

  const isExtendedTimezones =
    searchParams.get("EXTENDED_TIMEZONES")?.toLowerCase() === "true";

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Container>
        <Form
          onSubmit={handleSubmit(onSubmit)}
          aria-disabled={createUserMutation.isPending}
        >
          <Grid container columnSpacing={2}>
            <Grid item sm={6} xs={12}>
              <TextField
                name="name"
                label="Name"
                inputProps={{ ...register("name") }}
                fieldError={errors.name}
                autoFocus
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                name="email"
                label="E-mail"
                inputProps={{ ...register("email") }}
                fieldError={errors.email}
              />
            </Grid>
          </Grid>

          <Grid container columnSpacing={2}>
            <Grid item sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="countryState-label">State</InputLabel>
                <Controller
                  name="countryState"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="State"
                      labelId="countryState-label"
                      {...field}
                      error={!!errors.countryState?.message}
                    >
                      {usaStates.map((state) => (
                        <MenuItem
                          key={state.abbreviation}
                          value={state.abbreviation}
                        >
                          {state.abbreviation}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormErrorMessage>
                  {errors.countryState?.message ?? " "}
                </FormErrorMessage>
              </FormControl>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="timezone-label">Time Zone</InputLabel>
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => (
                    <Select label="Time Zone" {...field}>
                      <MenuItem key={"auto-detect"} value={"auto-detect"}>
                        Detect automatically
                      </MenuItem>
                      {timezones.map((timezone) => (
                        <MenuItem key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </MenuItem>
                      ))}
                      {isExtendedTimezones && (
                        <ListSubheader>Extended time zones</ListSubheader>
                      )}
                      {isExtendedTimezones &&
                        extendedTimezones.map((timezone) => (
                          <MenuItem key={timezone.value} value={timezone.value}>
                            {timezone.label}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
                <FormErrorMessage>
                  {errors.timezone?.message ?? " "}
                </FormErrorMessage>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container columnSpacing={2}>
            <Grid item sm={6} xs={12}>
              <TextField
                name="mlsId"
                label="License number (optional)"
                inputProps={{ ...register("mlsId") }}
                fieldError={errors.mlsId}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <PhoneNumberTextField
                name="phoneNumber"
                label="Phone number (optional)"
                inputProps={{ ...register("phoneNumber") }}
                fieldError={errors.phoneNumber}
              />
            </Grid>
          </Grid>

          <TextField
            name="officeName"
            label="Brokerage name"
            inputProps={{ ...register("officeName") }}
            fieldError={errors.officeName}
          />

          <Grid container>
            <Grid>
              <FormControl>
                <InputLabel>Email confirmed</InputLabel>
                <FormControlLabel
                  control={
                    <Controller
                      name="emailConfirmed"
                      control={control}
                      render={({ field }) => (
                        <Switch {...field} checked={field.value} />
                      )}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Mark the email as{" "}
                      <strong>
                        {emailConfirmed ? "verified" : "not verified"}
                      </strong>
                    </Typography>
                  }
                />
              </FormControl>
            </Grid>
          </Grid>

          <FormControl fullWidth>
            <InputLabel id="subscriptionPlanId-label">
              Subscription plan
            </InputLabel>
            {subscriptionPlansQuery.isLoading && (
              <Skeleton
                variant="rounded"
                component={Select}
                height={48}
                defaultValue={""}
              >
                <MenuItem value={""} />
              </Skeleton>
            )}
            {!subscriptionPlansQuery.isLoading && (
              <Controller
                name="subscriptionPlanId"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Subscription plan"
                    labelId="subscriptionPlanId-label"
                    readOnly={subscriptionPlans.length === 0}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      const plan = subscriptionPlans.find(
                        (p) => p.id === e.target.value,
                      );
                      setValue(
                        "enabledMlsProvidersSelected",
                        plan?.access.allowedMlsProviders ?? [],
                        { shouldValidate: true },
                      );
                    }}
                    error={!!errors.subscriptionPlanId?.message}
                  >
                    {subscriptionPlans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        <Box>
                          <Typography>{plan.name}</Typography>
                          <Typography variant="body2">
                            {plan.publicDescription}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            )}
            <FormErrorMessage>
              {errors.subscriptionPlanId?.message ?? " "}
            </FormErrorMessage>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="enabledMlsProvidersSelected-label">
              Enabled MLS Providers
            </InputLabel>
            <Controller
              name="enabledMlsProvidersSelected"
              control={control}
              render={({ field }) => (
                <Select
                  label="Enabled MLS Providers"
                  labelId="enabledMlsProvidersSelected-label"
                  multiple
                  renderValue={(selected) => selected.join(", ")}
                  {...field}
                  error={!!errors.enabledMlsProvidersSelected?.message}
                >
                  {selectedPlanAllowedProviders.map((mlsProvider) => (
                    <MenuItem
                      key={`provider-${mlsProvider}`}
                      value={mlsProvider}
                    >
                      <Checkbox checked={field.value?.includes(mlsProvider)} />
                      {mlsProvider}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormErrorMessage>
              {errors.enabledMlsProvidersSelected?.message ?? " "}
            </FormErrorMessage>
          </FormControl>

          <SubmitButton
            isLoading={createUserMutation.isPending}
            loadingText="Creating..."
            sx={{ justifySelf: "flex-start", gap: 1.5 }}
          >
            Create
          </SubmitButton>
        </Form>
      </Container>
    </PageLayout>
  );
}
